import { createApi } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import { Mutex } from "async-mutex";
import { setToken, logout } from "../store/slices/authSlice";
import { env } from "../config/env";

const mutex = new Mutex();

// RTK Query axios base query
const baseQuery = async ({ url, method, body, params, contentType = "application/json" }, api) => {
  const baseURL = env.apiUrl;
  const headers = { "Content-Type": contentType, Accept: "application/json" };

  try {
    const token = api.getState().auth.token;

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const result = await axios({
      url: `${baseURL}${url}`,
      method,
      data: !body ? null : (contentType === "application/json" 
          ? JSON.stringify(api.type === 'query' ? nullify(JSON.parse(JSON.stringify(body))) : nullify(body)) 
          : body),
      params,
      headers: {
        ...headers,
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0",
      },
    });

    return { data: result.data };
  } catch (error) {
    console.error(`API Error (${method} ${url}):`, error.response?.data || error.message);

    return {
      error: {
        status: error.response?.status,
        data: error.response?.data || error.message,
      },
    };
  }
};

const nullify = (obj) => {
  //nullifies the values even if they are deep inside
  if (obj != null && Array.isArray(obj)) {
    for (let i = 0; i < obj.length; i++) {
      if (typeof obj[i] === 'object' && obj[i] !== null && !Array.isArray(obj[i]))
        obj[i] = nullify(obj[i]);
    }
  } else if (obj != null && typeof obj == "object") {
    for (let i = 0, keys = Object.keys(obj); i < keys.length; i++) {
      obj[keys[i]] = nullify(obj[keys[i]]);
    }
  } else if (obj === "" && obj.length === 0 && typeof obj !== "boolean") {
    obj = null;
  }
  return obj;
};

export const baseQueryWithReauth = async (args, api) => {
  // wait until the mutex is available without locking it
  await mutex.waitForUnlock();

  let result = await baseQuery(args, api);
  if (result.error && result.error.status === 401) {
    // checking whether the mutex is locked
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        const refreshResult = await baseQuery(
          {
            url: "/auth/token",
            method: "POST",
            body: {
              grant_type: "refresh_token",
              refresh_token: api.getState().auth.refreshToken,
            },
            contentType: "application/x-www-form-urlencoded",
          },
          api
        );

        api.dispatch(
          setToken({
            token: refreshResult.data.access_token,
            refreshToken: refreshResult.data.refresh_token,
            expiresAt: refreshResult.data.expires_in ? Date.now() + refreshResult.data.expires_in * 1000 : null,
          })
        );
      } catch (e) {
        console.error("Token refresh failed:", e);
      } finally {
        // release must be called once the mutex should be released again.
        release();
      }

      // retry the initial query, log out if we get 401 again
      result = await baseQuery(args, api);
      if (result.error && result.error.status === 401) {
        api.dispatch(logout());
        window.location.href = "/";
      }
    } else {
      // wait until the mutex is available without locking it
      await mutex.waitForUnlock();
      result = await baseQuery(args, api);
    }
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: "api",
  baseQuery: async (args, api) => {
    var processedArgs = {};
    if (args.url === undefined) processedArgs.url = args;
    else processedArgs = args;
    return baseQueryWithReauth(processedArgs, api);
  },
  tagTypes: ["Drugs", 
    "LotNumbers", 
    "Strengths", 
    "User", 
    "Users", 
    "EnvironmentTestType", 
    "EnvironmentTestRecord",
    "CryptoCurrencies",
    "CryptoCurrency",
    "Recommendations",
    "Recommendation",
    "CryptoCurrencyRecommendations",
    "LatestRecommendation"
  ],
  keepUnusedDataFor: 120,
  refetchOnReconnect: false,
  refetchOnFocus: false,
  endpoints: () => ({})
});

export default baseApi;
